import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { ApiError, apiFetch } from "@/lib/api-client";
import { savePage, usePageDetail } from "@/hooks/use-pages";
import { usePageRecommendations } from "@/hooks/use-self-learning";
import {
  MAX_DYNAMIC_CONTENT_RULES_PER_PAGE,
  applyGeneratedVariantSets,
  collectAddressablePuckComponents,
  countDynamicContentRules,
  resolveDynamicPuckData,
  type PuckData,
  type VisitorContext,
} from "@ease/page-builder";

type RuleType =
  | "utm_campaign"
  | "utm_source"
  | "referral"
  | "returning_visitor"
  | "device"
  | "geo_language"
  | "time_of_day"
  | "contact_status";

interface PageBlock {
  type: string;
  props: Record<string, unknown>;
}

interface GeneratedVariant {
  id: string;
  label: string;
  badge: string;
  rule: { type: RuleType; value: string };
  overrides: Record<string, unknown>;
  priority: number;
}

interface GeneratedVariantSet {
  componentId: string;
  componentType: string;
  variants: GeneratedVariant[];
}

interface CampaignInputRow {
  name: string;
  audience?: string;
  utmValue?: string;
}

interface VariantsResponse {
  data: {
    pageId: string | null;
    targetComponentIds: string[];
    sets: GeneratedVariantSet[];
    totalVariants: number;
    projectedRuleCount: number;
    prompt: string;
  };
}

interface PreviewCase {
  id: string;
  label: string;
  description: string;
  context: VisitorContext;
}

const RULE_TYPES: Array<{ value: RuleType; label: string; badge: string }> = [
  { value: "utm_campaign", label: "UTM Campaign", badge: "UTM" },
  { value: "utm_source", label: "UTM Source", badge: "SRC" },
  { value: "device", label: "Device", badge: "DEV" },
  { value: "geo_language", label: "Geo / Language", badge: "GEO" },
  { value: "returning_visitor", label: "Returning Visitor", badge: "RET" },
  { value: "time_of_day", label: "Time of Day", badge: "TIME" },
  { value: "contact_status", label: "Contact Status", badge: "CRM" },
  { value: "referral", label: "Referral Source", badge: "REF" },
];

const DEFAULT_CAMPAIGNS = [
  "Back Pain | People with chronic back pain | back_pain",
  "Flexibility | People who want to move better | flexibility",
].join("\n");

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function extractBlocks(puckData: unknown): PageBlock[] {
  if (!isRecord(puckData) || !Array.isArray(puckData.content)) {
    return [];
  }

  return collectAddressablePuckComponents(puckData as PuckData)
    .map((component) => ({
      type: component.componentType,
      props: component.currentProps,
    }));
}

function blockSummary(block: PageBlock): string {
  const candidates = [
    block.props.headline,
    block.props.title,
    block.props.heading,
    block.props.buttonText,
    block.props.ctaText,
    block.props.label,
  ];

  const summary = candidates.find((value): value is string => typeof value === "string" && value.trim().length > 0);
  return summary ?? "No obvious marketing copy on this block yet";
}

function parseCampaigns(input: string): CampaignInputRow[] {
  const campaigns: CampaignInputRow[] = [];

  for (const rawLine of input.split("\n")) {
    const line = rawLine.trim();
    if (!line) {
      continue;
    }

    const [namePart, audiencePart, utmPart] = line.split("|").map((part) => part.trim());
    if (!namePart) {
      continue;
    }

    campaigns.push({
      name: namePart,
      audience: audiencePart || undefined,
      utmValue: utmPart || undefined,
    });
  }

  return campaigns;
}

const PREVIEW_CASES: PreviewCase[] = [
  {
    id: "default",
    label: "Default visitor",
    description: "No campaign match. Default block content should remain visible.",
    context: {
      utmCampaign: undefined,
      utmSource: undefined,
      utmMedium: undefined,
      referrer: undefined,
      referrerDomain: undefined,
      isReturningVisitor: false,
      device: "desktop",
      country: "US",
      language: "en",
      timeOfDay: "morning",
      contactStatus: "anonymous",
    },
  },
  {
    id: "campaign",
    label: "Campaign match",
    description: "Simulates a tagged ad click that should trigger the first matching UTM variant.",
    context: {
      utmCampaign: "back_pain",
      utmSource: "facebook",
      utmMedium: "paid_social",
      referrer: "https://facebook.com",
      referrerDomain: "facebook.com",
      isReturningVisitor: false,
      device: "desktop",
      country: "US",
      language: "en",
      timeOfDay: "morning",
      contactStatus: "anonymous",
    },
  },
  {
    id: "mobile",
    label: "Mobile returning visitor",
    description: "Simulates a returning mobile visitor with CRM-known state.",
    context: {
      utmCampaign: undefined,
      utmSource: "newsletter",
      utmMedium: "email",
      referrer: "https://mail.google.com",
      referrerDomain: "mail.google.com",
      isReturningVisitor: true,
      device: "mobile",
      country: "FR",
      language: "fr",
      timeOfDay: "evening",
      contactStatus: "known",
    },
  },
];

export default function DynamicVariants() {
  const { pageId } = useParams();
  const { page, isLoading, error } = usePageDetail(pageId);
  const {
    recommendations: optimizationRecommendations,
    error: recommendationsError,
  } = usePageRecommendations(pageId, {
    surface: "dashboard",
  });

  const [puckData, setPuckData] = useState<unknown>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [campaignInput, setCampaignInput] = useState(DEFAULT_CAMPAIGNS);
  const [ruleType, setRuleType] = useState<RuleType>("utm_campaign");
  const [generatedSets, setGeneratedSets] = useState<GeneratedVariantSet[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [previewCaseId, setPreviewCaseId] = useState<string>(PREVIEW_CASES[0]!.id);

  useEffect(() => {
    if (!page) {
      return;
    }

    setPuckData(page.content);
  }, [page]);

  const blocks = useMemo(() => extractBlocks(puckData), [puckData]);

  useEffect(() => {
    if (blocks.length === 0) {
      setSelectedIds([]);
      return;
    }

    setSelectedIds((current) => {
      if (current.length > 0) {
        return current.filter((id) => blocks.some((block) => block.props.id === id));
      }

      return blocks.slice(0, Math.min(3, blocks.length)).map((block) => block.props.id as string);
    });
  }, [blocks]);

  const selectedCount = selectedIds.length;
  const parsedCampaigns = useMemo(() => parseCampaigns(campaignInput), [campaignInput]);
  const previewCase = useMemo(
    () => PREVIEW_CASES.find((entry) => entry.id === previewCaseId) ?? PREVIEW_CASES[0]!,
    [previewCaseId],
  );
  const conversionInsight = useMemo(
    () => optimizationRecommendations.find((item) => item.category === "conversion" && item.upliftInsight)?.upliftInsight ?? null,
    [optimizationRecommendations],
  );
  const currentRuleCount = useMemo(
    () => (isRecord(puckData) ? countDynamicContentRules(puckData as PuckData) : 0),
    [puckData],
  );
  const previewPuckData = useMemo(() => {
    if (!isRecord(puckData) || !Array.isArray((puckData as PuckData).content)) {
      return null;
    }

    const merged = generatedSets.length > 0
      ? applyGeneratedVariantSets(puckData as PuckData, generatedSets)
      : puckData as PuckData;

    return resolveDynamicPuckData(merged, previewCase.context);
  }, [generatedSets, previewCase.context, puckData]);
  const previewBlocks = useMemo(() => {
    if (!previewPuckData) {
      return [];
    }

    return collectAddressablePuckComponents(previewPuckData.puckData).filter((block) => selectedIds.includes(block.componentId));
  }, [previewPuckData, selectedIds]);

  const handleToggleBlock = useCallback((componentId: string) => {
    setSelectedIds((current) => (
      current.includes(componentId)
        ? current.filter((id) => id !== componentId)
        : [...current, componentId]
    ));
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!pageId || !puckData || selectedCount === 0 || parsedCampaigns.length === 0) {
      setFeedback("Select at least one live block and at least one campaign.");
      return;
    }

    setIsGenerating(true);
    setFeedback(null);

    try {
      const response = await apiFetch<VariantsResponse>("/crm/pages/generate/variants", {
        method: "POST",
        body: JSON.stringify({
          pageId,
          currentPuckData: puckData,
          targetComponentIds: selectedIds,
          campaigns: parsedCampaigns,
          ruleType,
        }),
      });

      setGeneratedSets(response.data.sets);
      setFeedback(`Generated ${response.data.totalVariants} variants across ${response.data.sets.length} blocks. Projected page rule count: ${response.data.projectedRuleCount}/${MAX_DYNAMIC_CONTENT_RULES_PER_PAGE}.`);
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : "Variant generation failed.");
    } finally {
      setIsGenerating(false);
    }
  }, [pageId, puckData, parsedCampaigns, ruleType, selectedCount, selectedIds]);

  const handleSave = useCallback(async () => {
    if (!page || !isRecord(puckData) || generatedSets.length === 0) {
      return;
    }

    const nextPuckData = applyGeneratedVariantSets(puckData as PuckData, generatedSets);
    const nextRuleCount = countDynamicContentRules(nextPuckData);

    if (nextRuleCount > MAX_DYNAMIC_CONTENT_RULES_PER_PAGE) {
      setFeedback(`Dynamic content rules are capped at ${MAX_DYNAMIC_CONTENT_RULES_PER_PAGE} per page. This save would create ${nextRuleCount}. Remove or replace some variants first.`);
      return;
    }

    setIsSaving(true);
    setFeedback(null);

    try {
      await savePage(page.id, nextPuckData);
      setPuckData(nextPuckData);
      setFeedback(`Saved ${generatedSets.reduce((sum, set) => sum + set.variants.length, 0)} variants to page "${page.title}". The page now has ${nextRuleCount}/${MAX_DYNAMIC_CONTENT_RULES_PER_PAGE} dynamic rules.`);
    } catch (err) {
      if (err instanceof ApiError && err.errorCode === "max_variants") {
        setFeedback(err.message);
      } else {
        setFeedback(err instanceof Error ? err.message : "Saving variants failed.");
      }
    } finally {
      setIsSaving(false);
    }
  }, [generatedSets, page, puckData]);

  if (!pageId) {
    return <div className="p-6">Open this screen from a page route so it has a real builder page to work with.</div>;
  }

  return (
    <div className="mx-auto max-w-6xl p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dynamic Content Variants</h1>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          Generate audience-specific variants against the current saved builder state, then review and save them into the page version history.
        </p>
      </div>

      {isLoading ? <div className="rounded-lg border border-border p-4 text-sm">Loading page state…</div> : null}
      {error ? <div className="rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">{error}</div> : null}

      {page ? (
        <>
          <div className="rounded-xl border border-border bg-background p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Page</p>
                <p className="text-lg font-semibold">{page.title}</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">/{page.slug}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="rounded-lg bg-[var(--color-ai)]/8 px-3 py-2 text-xs text-[var(--color-foreground)]">
                  {blocks.length} live blocks with real `props.id`
                </div>
                <div className="rounded-lg border border-border px-3 py-2 text-xs text-[var(--color-foreground)]">
                  {currentRuleCount}/{MAX_DYNAMIC_CONTENT_RULES_PER_PAGE} dynamic rules saved
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <section className="space-y-4 rounded-xl border border-border bg-background p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold">1. Choose Builder Blocks</h2>
                  <p className="text-xs text-[var(--color-muted-foreground)]">Only selected blocks are sent to AI. Variants stay tied to their real block IDs.</p>
                </div>
                <div className="text-xs text-[var(--color-muted-foreground)]">{selectedCount} selected</div>
              </div>

              <div className="space-y-3">
                {blocks.map((block) => {
                  const componentId = block.props.id as string;
                  const isSelected = selectedIds.includes(componentId);

                  return (
                    <label
                      key={componentId}
                      className={`block cursor-pointer rounded-lg border p-3 transition ${isSelected ? "border-[var(--color-ai)] bg-[var(--color-ai)]/5" : "border-border bg-[var(--color-surface)]"}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleBlock(componentId)}
                          className="mt-1"
                        />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-[var(--color-foreground)]">{block.type}</span>
                            <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 font-mono text-[10px] text-[var(--color-muted-foreground)]">{componentId}</span>
                          </div>
                          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{blockSummary(block)}</p>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </section>

            <section className="space-y-4 rounded-xl border border-border bg-background p-4">
              <div>
                <h2 className="text-sm font-semibold">2. Campaign Inputs</h2>
                <p className="text-xs text-[var(--color-muted-foreground)]">Use one campaign per line: `Name | audience | utm_value`.</p>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)]">Rule Type</label>
                <select
                  value={ruleType}
                  onChange={(event) => setRuleType(event.target.value as RuleType)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  {RULE_TYPES.map((rule) => (
                    <option key={rule.value} value={rule.value}>
                      {rule.badge} {rule.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)]">Campaigns</label>
                <textarea
                  value={campaignInput}
                  onChange={(event) => setCampaignInput(event.target.value)}
                  rows={8}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
                <p className="text-xs text-[var(--color-muted-foreground)]">{parsedCampaigns.length} valid campaign rows parsed</p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={isGenerating || selectedCount === 0 || parsedCampaigns.length === 0}
                  className="flex-1 rounded-lg bg-[var(--color-ai)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {isGenerating ? "Generating…" : "Generate Variants"}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving || generatedSets.length === 0}
                  className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-medium disabled:opacity-50"
                >
                  {isSaving ? "Saving…" : "Save To Page"}
                </button>
              </div>

              {feedback ? (
                <div className="rounded-lg border border-border bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-foreground)]">
                  {feedback}
                </div>
              ) : null}
            </section>
          </div>

          <section className="space-y-4 rounded-xl border border-border bg-background p-4">
            <div>
              <h2 className="text-sm font-semibold">3. Review Generated Variants</h2>
              <p className="text-xs text-[var(--color-muted-foreground)]">Nothing is saved until you click “Save To Page”.</p>
            </div>

            <div className="grid gap-3 rounded-lg border border-border bg-[var(--color-surface)] p-3 md:grid-cols-[0.5fr_1fr]">
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-[var(--color-foreground)]">Preview as...</label>
                <select
                  value={previewCaseId}
                  onChange={(event) => setPreviewCaseId(event.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  {PREVIEW_CASES.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-xs font-semibold text-[var(--color-foreground)]">Preview resolution</p>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{previewCase.description}</p>
                <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                  Matching uses the same shared runtime as `page-serve`, so this preview reflects actual visitor-context resolution instead of local-only mocks.
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-[var(--color-surface)] p-3">
              <p className="text-xs font-semibold text-[var(--color-foreground)]">Optimization evidence</p>
              {recommendationsError ? (
                <>
                  <p className="mt-1 text-sm text-[var(--color-foreground)]">Recommendation telemetry could not be loaded.</p>
                  <p className="mt-1 text-xs text-[var(--color-destructive)]">
                    {recommendationsError}
                  </p>
                </>
              ) : conversionInsight ? (
                <>
                  <p className="mt-1 text-sm text-[var(--color-foreground)]">{conversionInsight.headline}</p>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{conversionInsight.summary}</p>
                  {conversionInsight.caveats[0] ? (
                    <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{conversionInsight.caveats[0]}</p>
                  ) : null}
                </>
              ) : (
                <>
                  <p className="mt-1 text-sm text-[var(--color-foreground)]">No measured uplift exists for these fresh variants yet.</p>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    Save the variants, then compare them through a real experiment before claiming a winner.
                  </p>
                </>
              )}
            </div>

            {generatedSets.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-6 text-sm text-[var(--color-muted-foreground)]">
                No generated variants yet. Select live blocks, enter campaigns, and run generation.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-lg border border-border bg-[var(--color-surface)] p-4">
                  <p className="text-xs font-semibold text-[var(--color-foreground)]">Resolved preview output</p>
                  {previewBlocks.length === 0 ? (
                    <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
                      No selected blocks are currently addressable in the preview payload.
                    </p>
                  ) : (
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      {previewBlocks.map((block) => {
                        const componentId = block.componentId;
                        return (
                          <div key={componentId} className="rounded-lg border border-border bg-background p-3">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-[var(--color-foreground)]">{block.componentType}</span>
                              <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 font-mono text-[10px] text-[var(--color-muted-foreground)]">{componentId}</span>
                            </div>
                            {block.zoneId ? (
                              <p className="mt-2 text-[11px] text-[var(--color-muted-foreground)]">Zone: {block.zoneId}</p>
                            ) : null}
                            <pre className="mt-3 overflow-x-auto rounded bg-[var(--color-surface)] p-3 text-xs text-[var(--color-foreground)]">
                              {JSON.stringify(block.currentProps, null, 2)}
                            </pre>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {generatedSets.map((set) => (
                  <div key={set.componentId} className="rounded-lg border border-border p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{set.componentType}</span>
                      <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 font-mono text-[10px] text-[var(--color-muted-foreground)]">{set.componentId}</span>
                    </div>

                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      {set.variants.map((variant) => (
                        <div key={variant.id} className="rounded-lg bg-[var(--color-surface)] p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="font-medium">{variant.label}</div>
                            <div className="text-xs text-[var(--color-muted-foreground)]">{variant.badge}</div>
                          </div>
                          <div className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                            Trigger: {variant.rule.type} = {variant.rule.value}
                          </div>
                          <pre className="mt-3 overflow-x-auto rounded bg-[var(--color-background)] p-3 text-xs text-[var(--color-foreground)]">
                            {JSON.stringify(variant.overrides, null, 2)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
